import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../apiConfig';

const OrderCard = ({ order, onUpdate, onRemoveWorker, onOpenRevision, onOpenFinish }) => {
    const { t } = useTranslation();
    const [workers, setWorkers] = useState([]);
    const [timeLeft, setTimeLeft] = useState("");
    const [delay, setDelay] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    
    // 📱 Mobile-ի համար վիճակ սահմանելը
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 850);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (order && order.workers) {
            setWorkers(Array.isArray(order.workers) ? order.workers : []);
        }
    }, [order]);

    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    });

    const statusMap = {
        new: { color: '#FF9933', label: t('status.new'), bg: 'rgba(255, 153, 51, 0.1)', icon: '✨', shadow: '0 4px 15px rgba(255, 153, 51, 0.2)' },
        processing: { color: '#4318FF', label: t('status.processing'), bg: 'rgba(67, 24, 255, 0.1)', icon: '⚙️', shadow: '0 4px 15px rgba(67, 24, 255, 0.2)' },
        pending_admin: { color: '#ecc94b', label: t('status.pending_admin'), bg: 'rgba(236, 201, 75, 0.1)', icon: '⏳', shadow: '0 4px 15px rgba(236, 201, 75, 0.2)' },
        confirmed: { color: '#007BFF', label: t('status.confirmed'), bg: 'rgba(0, 123, 255, 0.1)', icon: '✅', shadow: '0 4px 15px rgba(0, 123, 255, 0.2)' },
        mismatch: { color: '#E74C3C', label: t('status.mismatch'), bg: 'rgba(231, 76, 60, 0.1)', icon: '🚨', shadow: '0 4px 15px rgba(231, 76, 60, 0.3)' },
        modified: { color: '#D69E2E', label: t('status.modified'), bg: 'rgba(214, 158, 46, 0.1)', icon: '🔄', shadow: '0 4px 15px rgba(214, 158, 46, 0.2)' },
        in_progress: { color: '#9b59b6', label: t('status.in_progress'), bg: 'rgba(155, 89, 182, 0.1)', icon: '🚀', shadow: '0 4px 15px rgba(155, 89, 182, 0.2)' },
        pending_finish: { color: '#E91E63', label: t('status.pending_finish'), bg: 'rgba(233, 30, 99, 0.1)', icon: '🏁', shadow: '0 4px 15px rgba(233, 30, 99, 0.2)' },
        finished: { color: '#01B574', label: t('status.finished'), bg: 'rgba(1, 181, 116, 0.1)', icon: '🏆', shadow: '0 4px 15px rgba(1, 181, 116, 0.2)' },
        cancelled: { color: '#EE5D50', label: t('status.cancelled'), bg: 'rgba(238, 93, 80, 0.1)', icon: '✖️', shadow: '0 4px 15px rgba(238, 93, 80, 0.2)' }
    };

    const s = order.status?.toLowerCase().trim() || 'new';
    const conf = statusMap[s] || statusMap.new;

    useEffect(() => {
        if (s !== 'confirmed') { setTimeLeft(""); setDelay(null); return; }
        const timer = setInterval(() => {
            if (!order.order_time || !order.order_date) return;
            const [h, m] = order.order_time.split(':');
            const target = new Date(order.order_date);
            target.setHours(parseInt(h), parseInt(m), 0);
            const now = new Date();
            const diff = target - now;

            if (diff > 0) {
                const mins = Math.floor(diff / 60000);
                const secs = Math.floor((diff % 60000) / 1000);
                setTimeLeft(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
                setDelay(null);
            } else {
                const abs = Math.abs(diff);
                const lMins = Math.floor(abs / 60000);
                const lSecs = Math.floor((abs % 60000) / 1000);
                setDelay(`${lMins}${t('time.m')} ${lSecs}${t('time.s')}`);
                setTimeLeft("");
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [order.order_date, order.order_time, s, t]);

    const deleteWorker = async (wId) => {
        if (!window.confirm(t('confirm.remove_worker'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/orders/remove-worker/${order.id}/${wId}`, { 
                method: 'DELETE', 
                headers: getAuthHeaders() 
            });
            const data = await res.json();
            if (data.success) {
                setWorkers(prev => prev.filter(w => w.worker_id !== wId));
                if (onRemoveWorker) onRemoveWorker(order.id, wId);
            }
        } catch (e) { console.error("Delete worker error:", e); }
    };

    const getImg = (n) => {
        if (!n || n === 'null' || n === 'undefined') return 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
        return n.startsWith('http') ? n : `${API_BASE}${n.startsWith('/') ? '' : '/'}${n}`;
    };

    const formatDate = (dStr) => {
        if (!dStr) return '';
        return new Date(dStr).toLocaleDateString('hy-AM', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // 🎨 Դինամիկ սթայլեր հաշվի առնելով Mobile-ը
    const cardDynamicStyle = {
        ...styles.card,
        borderLeftColor: conf.color,
        transform: isHovered && !isMobile ? 'translateY(-5px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.12)' : '0 10px 30px rgba(0,0,0,0.06)',
        width: isMobile ? '96%' : '98%',
        margin: isMobile ? '15px auto' : '25px auto',
        borderRadius: isMobile ? '25px' : '35px'
    };

    const mainContentDynamicStyle = {
        ...styles.mainContent,
        flexDirection: isMobile ? 'column' : 'row'
    };

    const columnDynamicStyle = (isMiddle) => ({
        ...styles.column,
        borderLeft: isMiddle && !isMobile ? '1px solid #F4F7FE' : 'none',
        borderRight: isMiddle && !isMobile ? '1px solid #F4F7FE' : 'none',
        borderBottom: isMobile ? '1px solid #F4F7FE' : 'none',
        background: isMiddle ? '#FAFBFF' : '#ffffff',
        padding: isMobile ? '20px' : '30px',
        minWidth: isMobile ? '100%' : '320px'
    });

    return (
        <div 
            style={cardDynamicStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div style={{...styles.topBar, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0', alignItems: isMobile ? 'flex-start' : 'center'}}>
                <div style={{ ...styles.statusBadge, backgroundColor: conf.bg, color: conf.color, boxShadow: conf.shadow }}>
                    <span style={{marginRight: '8px'}}>{conf.icon}</span>
                    {conf.label}
                </div>
                <div style={styles.orderIdTag}>{t('order.id_label')} <span style={{color: '#1B2559'}}>{order.id}</span></div>
            </div>

            <div style={mainContentDynamicStyle}>
                {/* COLUMN 1: Customer Info */}
                <div style={columnDynamicStyle(false)}>
                    <div style={styles.sectionLabel}>{t('section.customer')}</div>
                    <div style={styles.customerCard}>
                        <div style={styles.avatarWrapper}>
                            <img src={getImg(order.customer_image)} style={styles.avatar} alt="User" />
                            <div style={styles.onlineDot}></div>
                        </div>
                        <div style={styles.customerMeta}>
                            <div style={styles.customerName}>{order.customer_name || t('common.no_name')}</div>
                            <a href={`tel:${order.customer_phone}`} style={styles.phoneLink}>
                                <span style={styles.phoneIcon}>📞</span> {order.customer_phone}
                            </a>
                        </div>
                    </div>
                    <div style={styles.serviceGlassBox}>
                        <div style={styles.categoryTag}>{order.category_name}</div>
                        <div style={styles.serviceTitle}>{order.service_name}</div>
                        <div style={styles.locationBox}><span style={{fontSize: '18px'}}>📍</span><span style={styles.locationText}>{order.location}</span></div>
                    </div>
                </div>

                {/* COLUMN 2: Details & Actions */}
                <div style={columnDynamicStyle(true)}>
                    <div style={styles.sectionLabel}>{t('section.details')}</div>
                    <div style={styles.infoPillsContainer}>
                        {order.cancel_reason && (
                            <div style={{...styles.luxuryPill, background: '#FFF5F5', borderColor: '#FC8181', color: '#C53030'}}>
                                <strong>{t('common.note')}:</strong> {order.cancel_reason}
                            </div>
                        )}
                        {order.message?.split('|').map((msg, idx) => (
                            <div key={idx} style={styles.luxuryPill}><div style={styles.pillBullet}></div>{msg.trim()}</div>
                        )) || <div style={styles.noData}>{t('common.no_data')}</div>}
                    </div>

                    <div style={styles.priceHighlightBox}>
                        <div style={styles.priceLabel}>{t('order.total_price')}</div>
                        <div style={styles.priceValue}>{order.total_price?.toLocaleString()} <small>֏</small></div>
                    </div>

                    <div style={styles.managementActions}>
                        <div style={styles.btnGrid}>
                            {s === 'new' && <button onClick={() => onUpdate(order.id, 'processing')} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #4318FF 0%, #5E3AFF 100%)'}}>{t('btn.accept')}</button>}
                            {s === 'processing' && <button onClick={() => onUpdate(order.id, 'confirmed')} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #007BFF 0%, #0056b3 100%)'}}>{t('btn.confirm')}</button>}
                            {s === 'pending_admin' && <button onClick={() => onUpdate(order.id, 'confirmed')} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #007BFF 0%, #0056b3 100%)'}}>{t('btn.confirm_order')}</button>}
                            
                            {s === 'confirmed' && (
                                <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                    <button onClick={() => onUpdate(order.id, 'in_progress')} style={{...styles.mainBtn, flex: 2, background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)'}}>{t('btn.match')}</button>
                                    <button onClick={() => onUpdate(order.id, 'mismatch')} style={{...styles.mainBtn, flex: 1, background: 'linear-gradient(135deg, #E74C3C 0%, #c0392b 100%)'}}>{t('btn.problem')}</button>
                                </div>
                            )}
                            {s === 'pending_finish' && (
                                <button onClick={() => onOpenFinish(order)} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)', boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'}}>🏆 {t('btn.confirm_finish')}</button>
                            )}
                            {s === 'mismatch' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                    <button onClick={() => onUpdate(order.id, 'in_progress')} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #01B574 0%, #01945e 100%)'}}>{t('btn.continue_old')}</button>
                                    <button onClick={() => onOpenRevision(order)} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)'}}>{t('btn.recalculate')}</button>
                                </div>
                            )}
                            {s === 'in_progress' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                    <button onClick={() => onUpdate(order.id, 'pending_finish')} style={{...styles.mainBtn, background: 'linear-gradient(135deg, #01B574 0%, #01945e 100%)'}}>{t('btn.finish')}</button>
                                    <button onClick={() => onOpenRevision(order)} style={{...styles.secondaryBtn, borderColor: '#D69E2E', color: '#D69E2E'}}>🔄 {t('btn.change_progress')}</button>
                                </div>
                            )}
                            {['new', 'processing', 'pending_admin', 'confirmed', 'in_progress', 'mismatch'].includes(s) && (
                                <button onClick={() => { const reason = window.prompt(t('common.reason_prompt')); if (reason) onUpdate(order.id, 'cancelled', reason); }} style={{...styles.secondaryBtn, marginTop: '8px'}}>{t('btn.reject')}</button>
                            )}
                        </div>
                    </div>
                </div>

                {/* COLUMN 3: Workers & Finance */}
                <div style={columnDynamicStyle(false)}>
                    <div style={styles.sectionLabel}>
                        {t('section.workers')} ({workers.length} / {(Number(order.wCount) || 0) + (Number(order.dCount) || 0)})
                    </div>
                    
                    <div style={styles.workerListScroll}>
                        {workers.length > 0 ? workers.map((worker, index) => (
                            <div key={index} style={styles.workerLuxuryCard}>
                                <img src={getImg(worker.profile_pic)} style={styles.workerAvatar} alt="W" />
                                <div style={styles.workerInfo}>
                                    <div style={styles.workerName}>{worker.full_name || t('common.no_name')}</div>
                                    <div style={styles.workerStatus}>{worker.role === 'driver' ? t('role.driver') : t('role.worker')} | {worker.phone || '---'}</div>
                                </div>
                               {s !== 'finished' && s !== 'cancelled' && (
                                    <button onClick={() => deleteWorker(worker.worker_id)} style={styles.workerDeleteBtn}>✕</button>
                                )}
                            </div>
                        )) : <div style={styles.emptyWorkerBox}>{t('common.no_worker')}</div>}
                    </div>

                    <div style={styles.financeMiniCard}>
                        <div style={styles.financeRow}><span style={styles.finLabel}>{t('order.car')} ({order.accepted_drivers_count || 0}/{order.dCount || 0}):</span><span style={styles.finValue}>{order.car_price?.toLocaleString()} ֏</span></div>
                        <div style={styles.financeRow}><span style={styles.finLabel}>{t('order.worker')} ({order.accepted_count || 0}/{order.wCount || 0}):</span><span style={styles.finValue}>{order.worker_wage?.toLocaleString()} ֏</span></div>
                    </div>

                    <div style={styles.timeAnalyticsBox}>
                        <div style={styles.timeHeader}>
                            <div style={styles.timeBlock}>📅 {formatDate(order.order_date)}</div>
                            <div style={styles.timeBlock}>⏰ {order.order_time}</div>
                        </div>
                        {s === 'confirmed' && (timeLeft || delay) && (
                            <div style={{ ...styles.timerDisplay, background: delay ? '#FFF1F1' : '#F4F7FE', color: delay ? '#D32F2F' : '#4318FF', border: delay ? '1px solid #FFC1C1' : '1px solid #D6E0FF' }}>
                                <div style={styles.timerLabel}>{delay ? t('timer.delay') : t('timer.left')}</div>
                                <div style={styles.timerCountdown}>{delay || timeLeft}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    card: { background: '#ffffff', overflow: 'hidden', borderLeft: '12px solid', transition: 'all 0.4s', display: 'flex', flexDirection: 'column' },
    topBar: { display: 'flex', padding: '20px 35px', borderBottom: '1px solid #F4F7FE' },
    statusBadge: { padding: '10px 22px', borderRadius: '16px', fontSize: '13px', fontWeight: '900', display: 'flex', alignItems: 'center' },
    orderIdTag: { fontSize: '14px', fontWeight: 'bold', color: '#A3AED0' },
    mainContent: { display: 'flex', minHeight: '400px', flexWrap: 'wrap' },
    column: { flex: 1, display: 'flex', flexDirection: 'column' },
    sectionLabel: { fontSize: '11px', fontWeight: '900', color: '#A3AED0', marginBottom: '15px', letterSpacing: '1.5px' },
    customerCard: { display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '25px', background: '#fff', padding: '15px', borderRadius: '25px', boxShadow: '0 10px 25px rgba(0,0,0,0.03)' },
    avatarWrapper: { position: 'relative' },
    avatar: { width: '70px', height: '70px', borderRadius: '22px', objectFit: 'cover' },
    onlineDot: { width: '15px', height: '15px', background: '#01B574', border: '3px solid #fff', borderRadius: '50%', position: 'absolute', bottom: '0', right: '0' },
    customerMeta: { display: 'flex', flexDirection: 'column' },
    customerName: { fontSize: '20px', fontWeight: '900', color: '#1B2559' },
    phoneLink: { fontSize: '16px', color: '#4318FF', textDecoration: 'none', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '5px' },
    phoneIcon: { marginRight: '5px' },
    serviceGlassBox: { padding: '25px', borderRadius: '30px', background: '#fff', border: '1px solid #E9EDF7' },
    categoryTag: { fontSize: '11px', fontWeight: '900', color: '#4318FF', marginBottom: '10px' },
    serviceTitle: { fontSize: '22px', fontWeight: '900', color: '#1B2559', marginBottom: '15px' },
    locationBox: { display: 'flex', alignItems: 'center', gap: '10px', color: '#718096' },
    locationText: { fontSize: '14px', fontWeight: '600' },
    infoPillsContainer: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' },
    luxuryPill: { background: '#fff', padding: '12px 18px', borderRadius: '15px', border: '1px solid #E9EDF7', fontSize: '13px', fontWeight: '700', color: '#2B3674' },
    pillBullet: { width: '6px', height: '6px', background: '#FF9933', borderRadius: '50%', marginRight: '10px', display: 'inline-block' },
    priceHighlightBox: { background: '#1B2559', padding: '20px', borderRadius: '25px', textAlign: 'center', marginBottom: '20px' },
    priceLabel: { fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' },
    priceValue: { fontSize: '28px', color: '#fff', fontWeight: '900' },
    managementActions: { marginTop: 'auto' },
    btnGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
    mainBtn: { width: '100%', padding: '16px', borderRadius: '18px', border: 'none', color: '#fff', fontSize: '14px', fontWeight: '900', cursor: 'pointer', transition: '0.3s' },
    secondaryBtn: { width: '100%', padding: '12px', borderRadius: '18px', border: '1px solid #E9EDF7', background: '#fff', color: '#EE5D50', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    workerListScroll: { maxHeight: '180px', overflowY: 'auto', marginBottom: '15px' },
    workerLuxuryCard: { display: 'flex', alignItems: 'center', background: '#fff', padding: '10px 15px', borderRadius: '18px', marginBottom: '8px', border: '1px solid #E9EDF7' },
    workerAvatar: { width: '40px', height: '40px', borderRadius: '12px', marginRight: '12px' },
    workerInfo: { flex: 1 },
    workerName: { fontSize: '14px', fontWeight: '800' },
    workerStatus: { fontSize: '10px', color: '#718096' },
    workerDeleteBtn: { background: '#FFF1F1', border: 'none', color: '#EE5D50', borderRadius: '8px', cursor: 'pointer', padding: '5px 8px' },
    financeMiniCard: { background: '#F4F7FE', borderRadius: '20px', padding: '15px', border: '1px dashed #4318FF', marginBottom: '15px' },
    financeRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '5px' },
    finLabel: { fontSize: '12px', color: '#718096', fontWeight: '600' },
    finValue: { fontSize: '13px', color: '#1B2559', fontWeight: '800' },
    timeAnalyticsBox: { marginTop: '10px' },
    timeHeader: { display: 'flex', gap: '10px', marginBottom: '10px' },
    timeBlock: { flex: 1, background: '#F4F7FE', padding: '10px', borderRadius: '12px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold' },
    timerDisplay: { padding: '15px', borderRadius: '20px', textAlign: 'center' },
    timerLabel: { fontSize: '10px', fontWeight: '900' },
    timerCountdown: { fontSize: '24px', fontWeight: '900' },
    emptyWorkerBox: { textAlign: 'center', padding: '20px', color: '#A3AED0', background: '#F8FAFC', borderRadius: '20px', border: '1px dashed #E9EDF7' },
    noData: { fontSize: '12px', color: '#A3AED0', textAlign: 'center' }
};

export default OrderCard;